<template>
  <div class="ma-4 px-4">
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="batchFilteredHeaders"
            :items="batches"
            fixed-header
            class="secondary row-height-50"
          >
            <template #[`item.action`]="{ item }">
              <v-icon
                small
                class="mr-2"
                color="accent"
                :disabled="!item.active"
                @click="editBatch(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <template #[`item.active`]="{ item }">
              <v-chip
                small
                :color="item.active ? 'green' : 'red'"
                dark
                @click="openConfirm(item)"
              >
                {{ item.active_status }}
              </v-chip>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <div class="pt-6">
      <span class="text-5xl font-weight-bold accent--text">
        Program Batch Term Schedules
      </span>
    </div>
    <searchBarFilter
      @add="create"
      :addField="validateAdd"
      @updateSearch="(v) => (search = v)"
      class="mt-2"
    />
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="programBatchTermScheduleHeaders"
            :search="search"
            :items="programBatchTermSchedules"
            fixed-header
            class="secondary row-height-50"
          >
            <template #[`item.action`]="{ item }">
              <v-icon
                small
                class="mr-2"
                color="accent"
                @click="editProgramBatchTermSchedule(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog v-model="termScheduleDialog" width="1000" persistent scrollable>
      <formDialog
        v-if="termScheduleDialog"
        @closeDialog="termScheduleDialog = false"
        @create="createProgramBatchTermSchedule"
        @update="updateProgramBatchTermSchedule"
        :termSchedule="termSchedule"
        :edit="editMode"
      />
    </v-dialog>
    <ConfirmDialog
      v-if="showDialog"
      :show="showDialog"
      @delete="changeConfirmation"
      @close="showDialog = false"
      title="batch"
      :name="currentItem.year + '-' + currentItem.batch"
      :active="currentItem.active"
    />
    <v-dialog v-model="batchDialog" width="800" persistent scrollable>
      <batchFormDialog
        v-if="batchDialog"
        @closeDialog="batchDialog = false"
        @update="updateBatch"
        :batch="batch"
        :programs="programs"
        :edit="editMode"
      />
    </v-dialog>
  </div>
</template>

<script>
import formDialog from "@/components/batch/termSchedule.vue";
import searchBarFilter from "@/components/searchBarFilter.vue";
import ConfirmDialog from "@/components/confirm.vue";
import batchFormDialog from "@/components/batch/form.vue";

import {
  batchHeaders,
  programBatchTermScheduleHeaders,
} from "@/constants/headers";

export default {
  components: {
    formDialog,
    searchBarFilter,
    ConfirmDialog,
    batchFormDialog,
  },
  props: {},
  data() {
    return {
      termScheduleDialog: false,
      termSchedule: {},
      programBatchTermSchedules: [],
      batches: [],
      batchDialog: false,
      batch: {},
      currentItem: {},
      showDialog: false,
      search: "",
      batchHeaders,
      batchFilteredHeaders: [],
      programBatchTermScheduleHeaders,
      programs: [],
      editMode: false,
      lastScheduleTerm: 1,
    };
  },
  watch: {},
  computed: {
    validateAdd() {
      if (!this.batches.length || !this.programs.length) return false;

      const batch = this.batches[0];
      const program = this.programs.find((item) => item.id == batch.program_id);

      if (!program || !program.duration) return false;

      return this.programBatchTermSchedules.length < program.duration;
    },
  },
  created() {
    this.batch_id = this.$route.query.batch_id;
    this.batchFilteredHeaders = this.batchHeaders.filter(
      (item) => item.value !== "set_term",
    );
    this.getBatches();
    this.initialise();
  },
  methods: {
    showActiveStatus(item) {
      return item.active ? "Active" : "Inactive";
    },
    async initialise() {
      await Promise.all([
        this.getPrograms(),
        this.getProgramBatchTermSchedules(),
      ]);
    },
    async getBatches() {
      await this.$axios
        .get(`/program_batches/${this.batch_id}`)
        .then((res) => {
          const batch = res.data.data;
          this.batches = [batch];
          this.$store.commit("setBreadcrumbs", [
            { text: "Batches", to: { name: "Batches" } },
            {
              text: `${batch.year} - ${batch.batch} - Program Batch Term Schedules`,
            },
          ]);
        })
        .catch(async (error) => {
          this.$store.commit("showSnackbar", {
            message: error.response.data.message,
            color: "error",
          });
        });
    },
    async getProgramBatchTermSchedules() {
      await this.$axios
        .get("/program_batch_term_schedules?program_batch_id=" + this.batch_id)
        .then((res) => {
          this.programBatchTermSchedules = res.data.data.sort(
            (a, b) => a.term - b.term,
          );
          if (this.programBatchTermSchedules.length) {
            this.lastScheduleTerm =
              this.programBatchTermSchedules[
                this.programBatchTermSchedules.length - 1
              ]?.term + 1 || 1;
          }
        })
        .catch(async (error) => {
          this.$store.commit("showSnackbar", {
            message: error.response.data.message,
            color: "error",
          });
        });
    },
    async getPrograms() {
      await this.$axios
        .get("/programs")
        .then((res) => {
          this.programs = res.data.data;
        })
        .catch(async (error) => {
          this.$store.commit("showSnackbar", {
            message: error.response.data.message,
            color: "error",
          });
        });
    },
    async createProgramBatchTermSchedule(termSchedule) {
      termSchedule.program_batch_id = this.batch_id;
      await this.$axios
        .post("/program_batch_term_schedules", {
          program_batch_term_schedule: termSchedule,
        })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Program Batch Term Schedule created successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async updateProgramBatchTermSchedule(termSchedule) {
      await this.$axios
        .put("/program_batch_term_schedules/" + termSchedule.id, {
          program_batch_term_schedule: termSchedule,
        })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Program Batch Term Schedule updated successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async updateBatch(batch) {
      await this.$axios
        .put("program_batches/" + batch.id, { program_batch: batch })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Batch updated successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    changeConfirmation() {
      this.updateBatch({
        id: this.currentItem.id,
        active: !this.currentItem.active,
      });
    },
    openConfirm(item) {
      this.showDialog = true;
      this.currentItem = { ...item };
    },
    create() {
      this.termSchedule = {
        term: this.lastScheduleTerm,
      };
      this.termScheduleDialog = true;
      this.editMode = false;
    },
    resetValues() {
      this.showDialog = false;
      this.currentItem = {};
      this.batchDialog = false;
      this.batch = {};
      this.termSchedule = {};
      this.termScheduleDialog = false;
      this.editMode = false;
    },
    editBatch(item) {
      this.batch = { ...item };
      this.batchDialog = true;
      this.editMode = true;
    },
    editProgramBatchTermSchedule(item) {
      this.termSchedule = { ...item };
      this.termScheduleDialog = true;
      this.editMode = true;
    },
  },
};
</script>

<style></style>
